import { useId, useRef, useState, type ReactNode } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Building2,
  Check,
  FileText,
  ImageUp,
  LayoutList,
  ListPlus,
  Loader2,
  Palette,
  Plus,
  RefreshCw,
  TextQuote,
  Trash2,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useFieldDefs } from '@/hooks/useInventoryMaster'
import { usePoLogo, useUploadPoLogo } from '@/hooks/usePurchaseOrders'
import { addAttributeColumn, keyFromLabel, moveItem } from '@/lib/poTemplate/edit'
import { formatPoNumber } from '@/lib/poTemplate/format'
import { accentColor } from '@/lib/poTemplate/layout'
import {
  ACCENT_PRESETS,
  LOGO_HEIGHT_MAX_MM,
  LOGO_HEIGHT_MIN_MM,
  LOGO_TYPES,
  clampLogoHeight,
  logoFileProblem,
} from '@/lib/poTemplate/logo'
import { PO_PLACEHOLDERS } from '@/lib/poTemplate/placeholders'
import type {
  PoCustomFieldDef,
  PoCustomFieldType,
  PoNumberDatePart,
  PoTemplateLayout,
} from '@/types/purchaseOrders'

interface PanelsProps {
  layout: PoTemplateLayout
  onChange: (next: PoTemplateLayout) => void
  readOnly?: boolean
}

const LOCALES = ['en-GB', 'en-US', 'fr-BE', 'nl-BE', 'de-DE', 'fr-FR', 'nl-NL', 'es-ES', 'it-IT']
const DATE_PARTS: { value: PoNumberDatePart; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'YY', label: 'YY (26)' },
  { value: 'YYYY', label: 'YYYY (2026)' },
  { value: 'MMYY', label: 'MMYY (1026)' },
  { value: 'YYMM', label: 'YYMM (2610)' },
]
const FIELD_TYPES: PoCustomFieldType[] = ['text', 'number', 'date', 'select']

type SectionId = 'brand' | 'company' | 'shipTo' | 'document' | 'layout' | 'customFields' | 'text'

const NAV: { id: SectionId; label: string; icon: LucideIcon }[] = [
  { id: 'brand', label: 'Brand', icon: Palette },
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'shipTo', label: 'Ship-to', icon: Truck },
  { id: 'document', label: 'Document', icon: FileText },
  { id: 'layout', label: 'Layout', icon: LayoutList },
  { id: 'customFields', label: 'Custom fields', icon: ListPlus },
  { id: 'text', label: 'Text', icon: TextQuote },
]

/** '' is "not set" in a nullable template field. */
const nul = (v: string): string | null => (v.trim() === '' ? null : v)

/**
 * The left pane of the PO template editor (z8nrz7f7dz): a section nav and one
 * card at a time. Controlled — every edit hands a new layout up, and the
 * preview beside it redraws from that.
 *
 * The nav is plain buttons, deliberately not a nested `<Tabs>`: this renders
 * inside Configure's own tablist, and a second one breaks its e2e locators.
 */
export function PoTemplateEditorPanels({ layout, onChange, readOnly }: PanelsProps) {
  const [active, setActive] = useState<SectionId>('brand')
  const set = <K extends keyof PoTemplateLayout>(key: K, value: PoTemplateLayout[K]) => onChange({ ...layout, [key]: value })
  const doc = layout.document

  return (
    <div className='grid min-w-0 gap-4 lg:grid-cols-[160px_minmax(0,1fr)]'>
      <nav aria-label='Template sections' className='lg:sticky lg:top-4 lg:self-start'>
        <ul className='flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1 lg:flex-col lg:flex-nowrap'>
          {NAV.map(({ id, label, icon: Icon }) => (
            <li key={id}>
              <button
                type='button'
                aria-current={active === id ? 'page' : undefined}
                onClick={() => setActive(id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm font-medium transition-colors',
                  active === id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                <Icon className='h-4 w-4 shrink-0' />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <fieldset disabled={readOnly} className='min-w-0'>
        {active === 'brand' && (
          <Card title='Brand' hint='Your logo and accent colour, printed on every purchase order.'>
            <BrandingPanel layout={layout} onChange={onChange} readOnly={readOnly} />
          </Card>
        )}

        {active === 'company' && (
          <Card title='Company' hint='The buyer block: who the order comes from and where to send invoices.'>
            <div className='grid gap-3 sm:grid-cols-2'>
              <Text label='Legal name' value={layout.company.legalName} onChange={(v) => set('company', { ...layout.company, legalName: nul(v) })} />
              <Text label='Phone' value={layout.company.phone} onChange={(v) => set('company', { ...layout.company, phone: nul(v) })} />
              <Text label='Email' value={layout.company.email} onChange={(v) => set('company', { ...layout.company, email: nul(v) })} />
              <Text label='VAT / tax ID' value={layout.company.taxId} onChange={(v) => set('company', { ...layout.company, taxId: nul(v) })} />
              <Text label='Registration no.' value={layout.company.registration} onChange={(v) => set('company', { ...layout.company, registration: nul(v) })} />
            </div>
            <SubHeading>Address</SubHeading>
            <div className='grid gap-3 sm:grid-cols-2'>
              {(['street', 'postcode', 'city', 'country'] as const).map((k) => (
                <Text
                  key={k}
                  label={k[0].toUpperCase() + k.slice(1)}
                  value={layout.company.address?.[k] ?? null}
                  onChange={(v) => set('company', { ...layout.company, address: { ...(layout.company.address ?? {}), [k]: nul(v) } })}
                />
              ))}
            </div>
          </Card>
        )}

        {active === 'shipTo' && (
          <Card title='Default ship-to' hint='Where goods are delivered unless an order says otherwise.'>
            <div className='grid gap-3 sm:grid-cols-2'>
              {(['name', 'street', 'postcode', 'city', 'country', 'contact', 'phone', 'email'] as const).map((k) => (
                <Text
                  key={k}
                  label={k[0].toUpperCase() + k.slice(1)}
                  value={layout.shipTo[k]}
                  onChange={(v) => set('shipTo', { ...layout.shipTo, [k]: nul(v) })}
                />
              ))}
            </div>
          </Card>
        )}

        {active === 'document' && (
          <Card title='Document' hint='Title, paper, money formatting, numbering and the defaults new orders start with.'>
            <div className='grid gap-3 sm:grid-cols-2'>
              <Text label='Title' value={doc.title} onChange={(v) => set('document', { ...doc, title: v })} />
              <Pick
                label='Paper size'
                value={doc.paperSize}
                options={[{ value: 'A4', label: 'A4' }, { value: 'Letter', label: 'Letter' }]}
                onChange={(v) => set('document', { ...doc, paperSize: v as 'A4' | 'Letter' })}
              />
              <Text label='Locale (e.g. fr-BE)' value={doc.locale} list='po-locales' onChange={(v) => set('document', { ...doc, locale: v })} />
              <datalist id='po-locales'>
                {LOCALES.map((l) => <option key={l} value={l} />)}
              </datalist>
              <Text label='Currency' value={doc.currency} onChange={(v) => set('document', { ...doc, currency: v.toUpperCase().slice(0, 3) })} />
            </div>

            <SubHeading>Numbering</SubHeading>
            <div className='rounded-lg border border-slate-200 bg-slate-50/60 p-3'>
              <div className='grid grid-cols-2 gap-3 2xl:grid-cols-4'>
                <Text label='Prefix' value={doc.numbering.prefix} onChange={(v) => set('document', { ...doc, numbering: { ...doc.numbering, prefix: v } })} />
                <Pick
                  label='Date part'
                  value={doc.numbering.datePart}
                  options={DATE_PARTS}
                  onChange={(v) => set('document', { ...doc, numbering: { ...doc.numbering, datePart: v as PoNumberDatePart } })}
                />
                <Text label='Separator' value={doc.numbering.separator} maxLength={2} onChange={(v) => set('document', { ...doc, numbering: { ...doc.numbering, separator: v } })} />
                <Num
                  label='Digits'
                  value={doc.numbering.padding}
                  min={0}
                  max={8}
                  // The server takes a whole number of digits, 0–8.
                  onChange={(v) =>
                    set('document', {
                      ...doc,
                      numbering: { ...doc.numbering, padding: v == null ? 4 : Math.min(8, Math.max(0, Math.round(v))) },
                    })
                  }
                />
              </div>
              <p className='mt-3 text-xs text-slate-500'>
                Next numbers look like{' '}
                <span className='rounded bg-white px-1.5 py-0.5 font-mono font-semibold text-slate-900 ring-1 ring-slate-200' data-testid='po-number-sample'>
                  {formatPoNumber(doc.numbering, 1, new Date())}
                </span>
              </p>
            </div>

            <SubHeading>Defaults for new orders</SubHeading>
            <div className='grid gap-3 sm:grid-cols-2'>
              <Num
                label='Default VAT %'
                value={doc.defaults.taxRatePct}
                min={0}
                onChange={(v) => set('document', { ...doc, defaults: { ...doc.defaults, taxRatePct: v ?? 0 } })}
              />
              <Text label='Default payment terms' value={doc.defaults.paymentTerms} onChange={(v) => set('document', { ...doc, defaults: { ...doc.defaults, paymentTerms: nul(v) } })} />
              <Text label='Default Incoterms' value={doc.defaults.incoterms} onChange={(v) => set('document', { ...doc, defaults: { ...doc.defaults, incoterms: nul(v) } })} />
            </div>
          </Card>
        )}

        {active === 'layout' && (
          <div className='space-y-4'>
            <Card title='Sections' hint='Printed top to bottom in this order. Two address sections next to each other share a row.'>
              <ul className='divide-y divide-slate-100 rounded-lg border border-slate-200'>
                {layout.sections.map((s, i) => (
                  <li key={s.key} className='px-2 py-2'>
                    <OrderRow
                      visible={s.visible}
                      label={s.title}
                      hint={s.key}
                      onVisible={(visible) => set('sections', layout.sections.map((x, j) => (j === i ? { ...x, visible } : x)))}
                      onLabel={(title) => set('sections', layout.sections.map((x, j) => (j === i ? { ...x, title } : x)))}
                      onMove={(d) => set('sections', moveItem(layout.sections, i, d))}
                      first={i === 0}
                      last={i === layout.sections.length - 1}
                    />
                    {s.fields && s.fields.length > 0 && (
                      <details className='group mt-1.5 pl-[3.25rem]'>
                        <summary className='cursor-pointer select-none text-xs font-medium text-slate-500 hover:text-slate-800'>
                          Fields ({s.fields.length})
                        </summary>
                        <div className='mt-2 space-y-1.5 border-l border-slate-200 pl-3'>
                          {s.fields.map((f, fi) => {
                            const setField = (patch: Partial<typeof f>) =>
                              set(
                                'sections',
                                layout.sections.map((x, j) =>
                                  j === i ? { ...x, fields: x.fields?.map((y, k) => (k === fi ? { ...y, ...patch } : y)) } : x,
                                ),
                              )
                            return (
                              <div key={f.key} className='flex items-center gap-2'>
                                <Switch checked={f.visible} onCheckedChange={(visible) => setField({ visible })} aria-label={`Show ${f.label}`} />
                                <Input className='h-8 text-xs' value={f.label} onChange={(e) => setField({ label: e.target.value })} aria-label={`${f.key} label`} />
                                <span className='hidden w-24 shrink-0 truncate font-mono text-[11px] text-slate-400 sm:block' title={f.key}>{f.key}</span>
                              </div>
                            )
                          })}
                        </div>
                      </details>
                    )}
                  </li>
                ))}
              </ul>
            </Card>

            <Card title='Line columns' hint='The columns of the order lines table, left to right. Add a material attribute as a column of its own.'>
              <ul className='divide-y divide-slate-100 rounded-lg border border-slate-200'>
                {layout.columns.map((c, i) => (
                  <li key={c.key} className='flex items-center gap-2 px-2 py-2'>
                    <div className='min-w-0 flex-1'>
                      <OrderRow
                        visible={c.visible}
                        label={c.label}
                        hint={c.key}
                        onVisible={(visible) => set('columns', layout.columns.map((x, j) => (j === i ? { ...x, visible } : x)))}
                        onLabel={(label) => set('columns', layout.columns.map((x, j) => (j === i ? { ...x, label } : x)))}
                        onMove={(d) => set('columns', moveItem(layout.columns, i, d))}
                        first={i === 0}
                        last={i === layout.columns.length - 1}
                      />
                    </div>
                    {c.key.startsWith('attr:') ? (
                      <IconButton title='Remove column' onClick={() => set('columns', layout.columns.filter((_, j) => j !== i))} danger>
                        <Trash2 className='h-4 w-4' />
                      </IconButton>
                    ) : (
                      <span className='w-8 shrink-0' aria-hidden />
                    )}
                  </li>
                ))}
              </ul>
              <AttributeColumnPicker layout={layout} onChange={onChange} />
            </Card>
          </div>
        )}

        {active === 'customFields' && (
          <Card
            title='Custom fields'
            hint='Extra fields every order carries, filled in on the order and printed under Order terms. A required field must be filled before the order can be sent.'
          >
            <CustomFieldsPanel fields={layout.customFields} onChange={(customFields) => set('customFields', customFields)} />
          </Card>
        )}

        {active === 'text' && (
          <Card title='Text blocks' hint='Terms, default notes and the footer. Simple formatting and placeholders are supported.'>
            <div className='space-y-4'>
              {(
                [
                  ['terms', 'Terms & conditions'],
                  ['defaultNotes', 'Default notes (copied onto new orders)'],
                  ['footer', 'Footer'],
                ] as const
              ).map(([k, label]) => (
                <div key={k} className='space-y-1.5'>
                  <Label htmlFor={`po-text-${k}`} className={LABEL}>{label}</Label>
                  <Textarea
                    id={`po-text-${k}`}
                    rows={k === 'terms' ? 6 : 3}
                    className='font-mono text-xs'
                    value={layout.textBlocks[k] ?? ''}
                    onChange={(e) => set('textBlocks', { ...layout.textBlocks, [k]: e.target.value })}
                  />
                </div>
              ))}
              <details className='rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs'>
                <summary className='cursor-pointer select-none font-medium text-slate-700'>Placeholders and formatting</summary>
                <p className='mt-2 text-slate-500'>
                  Formatting: <code>**bold**</code>, <code>*italic*</code>, <code># Heading</code>, <code>## Subheading</code>,{' '}
                  <code>- item</code>, <code>1. item</code>. Anything else prints as written.
                </p>
                <ul className='mt-2 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2'>
                  {PO_PLACEHOLDERS.map((p) => (
                    <li key={p.key}>
                      <code className='text-[11px]'>{`{{${p.key}}}`}</code>{' '}
                      <span className='text-slate-500'>{p.description}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </Card>
        )}
      </fieldset>
    </div>
  )
}

const LABEL = 'text-xs font-medium text-slate-600'

function Card({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className='rounded-xl border border-slate-200 bg-white shadow-sm'>
      <header className='border-b border-slate-100 px-5 py-4'>
        <h3 className='text-sm font-semibold text-slate-900'>{title}</h3>
        <p className='mt-0.5 text-xs text-slate-500'>{hint}</p>
      </header>
      <div className='space-y-4 px-5 py-4'>{children}</div>
    </section>
  )
}

function SubHeading({ children }: { children: ReactNode }) {
  return <p className='pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400'>{children}</p>
}

function BrandingPanel({ layout, onChange, readOnly }: PanelsProps) {
  const b = layout.branding
  const setB = (patch: Partial<typeof b>) => onChange({ ...layout, branding: { ...b, ...patch } })
  const logoUrl = usePoLogo(b.logoAssetKey)
  const upload = useUploadPoLogo()
  const fileRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const blocked = !!readOnly || upload.isPending
  const heightId = useId()

  function pick(file: File | undefined) {
    if (!file || blocked) return
    setError(null)
    const problem = logoFileProblem(file)
    if (problem) return setError(problem)
    upload.mutate(file, {
      onSuccess: ({ logoAssetKey }) => setB({ logoAssetKey }),
      onError: (e) => setError(e instanceof Error ? e.message : 'The logo could not be uploaded.'),
    })
  }
  const browse = () => {
    if (!blocked) fileRef.current?.click()
  }

  return (
    <div className='space-y-5'>
      <input
        ref={fileRef}
        type='file'
        accept={LOGO_TYPES.join(',')}
        className='hidden'
        data-testid='po-logo-input'
        onChange={(e) => {
          pick(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      <div className='space-y-1.5'>
        <p className={LABEL}>Logo</p>
        {b.logoAssetKey ? (
          <div className='flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-3'>
            <div
              className='flex h-24 w-48 shrink-0 items-center justify-center rounded-lg border border-slate-200 p-2'
              style={{
                backgroundColor: '#fff',
                backgroundImage: 'conic-gradient(#f1f5f9 25%, transparent 0 50%, #f1f5f9 0 75%, transparent 0)',
                backgroundSize: '16px 16px',
              }}
            >
              {logoUrl ? (
                <img src={logoUrl} alt='Logo' className='max-h-20 max-w-44 object-contain' />
              ) : (
                <Loader2 className='h-4 w-4 animate-spin text-slate-400' />
              )}
            </div>
            <div className='flex min-w-0 flex-col gap-2'>
              <p className='text-xs text-slate-500'>Shown at the top of every order. PNG, JPG or SVG · up to 1 MB.</p>
              <div className='flex flex-wrap gap-2'>
                <Button size='sm' variant='outline' className='gap-1.5' disabled={blocked} onClick={browse}>
                  {upload.isPending ? <Loader2 className='h-4 w-4 animate-spin' /> : <RefreshCw className='h-4 w-4' />}
                  {upload.isPending ? 'Uploading…' : 'Replace'}
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  className='gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700'
                  disabled={blocked}
                  onClick={() => setB({ logoAssetKey: null })}
                >
                  <Trash2 className='h-4 w-4' />
                  Remove
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            role='button'
            tabIndex={blocked ? -1 : 0}
            aria-label='Upload logo'
            aria-disabled={blocked}
            onClick={browse}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                browse()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (!blocked) setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              pick(e.dataTransfer.files?.[0])
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
              blocked
                ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                : dragOver
                  ? 'cursor-copy border-slate-900 bg-slate-100'
                  : 'cursor-pointer border-slate-300 bg-slate-50/60 hover:border-slate-400 hover:bg-slate-50',
            )}
          >
            <span className='flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200'>
              {upload.isPending ? <Loader2 className='h-5 w-5 animate-spin text-slate-600' /> : <ImageUp className='h-5 w-5 text-slate-600' />}
            </span>
            {upload.isPending ? (
              <p className='text-sm font-medium text-slate-700'>Uploading…</p>
            ) : (
              <p className='text-sm text-slate-700'>
                <span className='font-semibold text-slate-900'>Click to upload</span> or drag and drop
              </p>
            )}
            <p className='text-xs text-slate-500'>PNG, JPG or SVG · up to 1 MB</p>
          </div>
        )}
        {error && (
          <p role='alert' className='text-xs font-medium text-destructive'>
            {error}
          </p>
        )}
        {!b.logoAssetKey && (
          <p className='text-xs text-slate-500'>
            No logo yet — the header prints your company name
            {layout.company.legalName ? <> (<span className='font-medium text-slate-700'>{layout.company.legalName}</span>)</> : null} on its own.
          </p>
        )}
      </div>

      <div className='grid gap-4 sm:grid-cols-2'>
        <div className='space-y-1.5'>
          <p className={LABEL}>Logo position</p>
          <div role='radiogroup' aria-label='Logo position' className='grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1'>
            {(['left', 'right'] as const).map((pos) => {
              const on = b.logoPosition === pos
              return (
                <button
                  key={pos}
                  type='button'
                  role='radio'
                  aria-checked={on}
                  onClick={() => setB({ logoPosition: pos })}
                  className={cn(
                    'flex h-9 items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors',
                    on ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
                  )}
                >
                  <PositionGlyph side={pos} />
                  {pos === 'left' ? 'Left' : 'Right'}
                </button>
              )
            })}
          </div>
        </div>

        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor={heightId} className={LABEL}>Logo height</Label>
            <span className='rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700'>{b.logoHeightMm} mm</span>
          </div>
          <div className='flex h-9 items-center'>
            <input
              id={heightId}
              type='range'
              min={LOGO_HEIGHT_MIN_MM}
              max={LOGO_HEIGHT_MAX_MM}
              step={1}
              value={clampLogoHeight(b.logoHeightMm)}
              onChange={(e) => setB({ logoHeightMm: clampLogoHeight(Number(e.target.value)) })}
              className='w-full cursor-pointer accent-slate-900 disabled:cursor-not-allowed'
            />
          </div>
        </div>
      </div>

      <AccentColor value={accentColor(layout)} custom={!!b.accentColor} readOnly={readOnly} onChange={(c) => setB({ accentColor: c })} />
    </div>
  )
}

/** A tiny page header: the logo block on `side`, title lines on the other. */
function PositionGlyph({ side }: { side: 'left' | 'right' }) {
  return (
    <span aria-hidden className={cn('flex h-4 w-6 items-center gap-0.5 rounded-sm border border-current p-0.5 opacity-70', side === 'right' && 'flex-row-reverse')}>
      <span className='h-full w-2 rounded-[1px] bg-current' />
      <span className='flex flex-1 flex-col gap-0.5'>
        <span className='h-px w-full bg-current' />
        <span className='h-px w-2/3 bg-current' />
      </span>
    </span>
  )
}

/** The document's one accent: rules, headings and the line-table header. Null restores the default. */
function AccentColor({
  value,
  custom,
  readOnly,
  onChange,
}: {
  value: string
  custom: boolean
  readOnly?: boolean
  onChange: (c: string | null) => void
}) {
  const id = useId()
  const isPreset = ACCENT_PRESETS.some((p) => p.value.toLowerCase() === value.toLowerCase())
  return (
    <div className='space-y-1.5'>
      <p className={LABEL}>Accent colour</p>
      <div className='flex flex-wrap items-center gap-2'>
        {ACCENT_PRESETS.map((p, i) => {
          const on = p.value.toLowerCase() === value.toLowerCase()
          return (
            <button
              key={p.value}
              type='button'
              title={p.name}
              aria-label={`Accent ${p.name}`}
              aria-pressed={on}
              // The first preset IS the default, so choosing it clears the override.
              onClick={() => onChange(i === 0 ? null : p.value)}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full ring-offset-2 transition-shadow disabled:cursor-not-allowed',
                on ? 'ring-2 ring-slate-900' : 'hover:ring-2 hover:ring-slate-300',
              )}
              style={{ backgroundColor: p.value }}
            >
              {on && <Check className='h-4 w-4 text-white' />}
            </button>
          )
        })}
        <span className='mx-1 h-6 w-px bg-slate-200' aria-hidden />
        <label
          htmlFor={id}
          title='Custom colour'
          className={cn(
            'relative flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full ring-offset-2',
            !isPreset ? 'ring-2 ring-slate-900' : 'ring-1 ring-slate-200 hover:ring-2 hover:ring-slate-300',
            readOnly && 'cursor-not-allowed',
          )}
          style={{ background: isPreset ? 'conic-gradient(#ef4444, #f59e0b, #22c55e, #3b82f6, #a855f7, #ef4444)' : value }}
        >
          <span className='sr-only'>Custom accent colour</span>
          <input
            id={id}
            type='color'
            className='absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed'
            value={value}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value)}
          />
        </label>
        <span className='font-mono text-xs text-slate-500'>{value}</span>
        {custom && !readOnly && (
          <Button size='sm' variant='ghost' className='h-7 px-2 text-xs' onClick={() => onChange(null)}>
            Use default
          </Button>
        )}
      </div>
      <p className='text-xs text-slate-500'>Used for rules, headings and the line table header.</p>
    </div>
  )
}

function AttributeColumnPicker({ layout, onChange }: Omit<PanelsProps, 'readOnly'>) {
  const { data: defs = [] } = useFieldDefs()
  const used = new Set(layout.columns.map((c) => c.key))
  const available = defs.filter((d) => !used.has(`attr:${d.key}`))
  if (available.length === 0) return null
  return (
    <Select value='' onValueChange={(key) => {
      const def = defs.find((d) => d.key === key)
      if (def) onChange(addAttributeColumn(layout, def.key, def.label))
    }}>
      <SelectTrigger className='h-9 w-full sm:w-64'>
        <SelectValue placeholder='Add attribute column…' />
      </SelectTrigger>
      <SelectContent>
        {available.map((d) => (
          <SelectItem key={`${d.profileType}:${d.key}`} value={d.key}>
            {d.label} <span className='text-muted-foreground'>({d.key})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function CustomFieldsPanel({ fields, onChange }: { fields: PoCustomFieldDef[]; onChange: (f: PoCustomFieldDef[]) => void }) {
  const setAt = (i: number, patch: Partial<PoCustomFieldDef>) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)))
  return (
    <div className='space-y-3'>
      {fields.length === 0 && (
        <p className='rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-500'>
          No custom fields yet.
        </p>
      )}
      {fields.map((f, i) => (
        <div key={i} className='space-y-3 rounded-lg border border-slate-200 p-3'>
          <div className='grid gap-3 sm:grid-cols-2'>
            <Text
              label='Label'
              value={f.label}
              onChange={(label) => setAt(i, { label, key: f.key && f.key !== keyFromLabel(f.label) ? f.key : keyFromLabel(label) })}
            />
            <Text label='Key' value={f.key} onChange={(key) => setAt(i, { key: key.replace(/[^A-Za-z0-9_]/g, '') })} />
            <Pick
              label='Type'
              value={f.type}
              options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
              onChange={(type) => setAt(i, { type: type as PoCustomFieldType, options: type === 'select' ? (f.options ?? []) : undefined })}
            />
            <div className='flex items-end justify-between gap-2'>
              <label className='flex h-9 items-center gap-2 text-sm text-slate-700'>
                <Switch checked={!!f.required} onCheckedChange={(required) => setAt(i, { required })} aria-label='Required' />
                Required
              </label>
              <IconButton title='Remove field' onClick={() => onChange(fields.filter((_, j) => j !== i))} danger>
                <Trash2 className='h-4 w-4' />
              </IconButton>
            </div>
            {f.type === 'select' && (
              <div className='sm:col-span-2'>
                <Text
                  label='Options (comma-separated)'
                  value={(f.options ?? []).join(', ')}
                  onChange={(v) => setAt(i, { options: v.split(',').map((o) => o.trim()) })}
                />
              </div>
            )}
          </div>
        </div>
      ))}
      <Button size='sm' variant='outline' className='gap-1.5' onClick={() => onChange([...fields, { key: '', label: '', type: 'text' }])}>
        <Plus className='h-4 w-4' />
        Add custom field
      </Button>
    </div>
  )
}

/** One reorderable row: up/down, visibility, an editable label and its key. */
function OrderRow({
  visible,
  label,
  hint,
  onVisible,
  onLabel,
  onMove,
  first,
  last,
}: {
  visible: boolean
  label: string
  hint: string
  onVisible: (v: boolean) => void
  onLabel: (v: string) => void
  onMove: (delta: number) => void
  first: boolean
  last: boolean
}) {
  return (
    <div className='flex items-center gap-2'>
      <div className='flex shrink-0 flex-col'>
        <button
          type='button'
          title='Move up'
          aria-label='Move up'
          disabled={first}
          onClick={() => onMove(-1)}
          className='flex h-4 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-30'
        >
          <ArrowUp className='h-3.5 w-3.5' />
        </button>
        <button
          type='button'
          title='Move down'
          aria-label='Move down'
          disabled={last}
          onClick={() => onMove(1)}
          className='flex h-4 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-30'
        >
          <ArrowDown className='h-3.5 w-3.5' />
        </button>
      </div>
      <Switch checked={visible} onCheckedChange={onVisible} aria-label={`Show ${hint}`} />
      <Input
        className={cn('h-9', !visible && 'text-slate-400')}
        value={label}
        onChange={(e) => onLabel(e.target.value)}
        aria-label={`${hint} title`}
      />
      <span className='hidden w-24 shrink-0 truncate font-mono text-[11px] text-slate-400 sm:block' title={hint}>{hint}</span>
    </div>
  )
}

function IconButton({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: ReactNode
}) {
  return (
    <button
      type='button'
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors disabled:opacity-30',
        danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-slate-100 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  )
}

function Text({
  label,
  value,
  onChange,
  list,
  maxLength,
}: {
  label: string
  value: string | null | undefined
  onChange: (v: string) => void
  list?: string
  maxLength?: number
}) {
  // useId, not the label: Company and Ship-to both have a "Street".
  const id = useId()
  return (
    <div className='min-w-0 space-y-1.5'>
      <Label htmlFor={id} className={LABEL}>{label}</Label>
      <Input id={id} className='h-9' value={value ?? ''} list={list} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}

function Num({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string
  value: number
  onChange: (v: number | null) => void
  min?: number
  max?: number
}) {
  const id = useId()
  return (
    <div className='min-w-0 space-y-1.5'>
      <Label htmlFor={id} className={LABEL}>{label}</Label>
      <Input
        id={id}
        className='h-9'
        type='number'
        min={min}
        max={max}
        step='any'
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </div>
  )
}

function Pick({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className='min-w-0 space-y-1.5'>
      <Label className={LABEL}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className='h-9' aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
